import { useEffect } from "react";
import { BackofficeNotification, NotificationKind } from "../context/BackofficeNotificationContext";
import { getTokenFromLocal } from "../helpers/appHelpers";

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  MANUAL_ADD: "นำเงินสดเข้า",
  MANUAL_REMOVE: "นำเงินสดออก",
  MOVE_TO_SAFE: "ฝากเข้าตู้นิรภัย",
  MOVE_FROM_SAFE: "ถอนจากตู้นิรภัย",
};

function buildMessage(kind: NotificationKind, payload: any): string {
  switch (kind) {
    case "cash_movement_pending": {
      const typeLabel = MOVEMENT_TYPE_LABELS[payload.movement_type] || payload.movement_type;
      return `คำขอ${typeLabel} ฿${Number(payload.amount).toLocaleString("th-TH", { minimumFractionDigits: 2 })} รออนุมัติ`;
    }
    case "cash_movement_decided": {
      const typeLabel = MOVEMENT_TYPE_LABELS[payload.movement_type] || payload.movement_type;
      const statusLabel = payload.status === "APPROVED" ? "อนุมัติ" : "ปฏิเสธ";
      return `คำขอ${typeLabel} ${statusLabel}แล้ว`;
    }
    case "approval":
      return "มีคำขออนุมัติ POS ใหม่";
    case "sale_return": {
      const statusLabel = payload.status || "";
      return `มีคำขอคืนสินค้า ${statusLabel}`;
    }
    default:
      return "มีการแจ้งเตือนใหม่";
  }
}

type AddNotificationFn = (n: Omit<BackofficeNotification, "id" | "read" | "at">) => void;

/**
 * Single persistent SSE connection to the global backoffice event stream.
 * Accepts addNotification directly to avoid reading the default (no-op) context
 * when called from inside BackofficeNotificationProvider itself.
 */
export function useBackofficeSSE(addNotification: AddNotificationFn) {
  useEffect(() => {
    let es: EventSource | null = null;
    let retryDelay = 2000;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let active = true;

    function connect() {
      if (!active) return;
      const token = getTokenFromLocal() || "";
      if (!token) {
        retryTimer = setTimeout(connect, 3000);
        return;
      }
      const baseUrl = import.meta.env.VITE_FRONT_API as string;
      const url = `${baseUrl}/sse/backoffice?token=${encodeURIComponent(token)}`;

      es = new EventSource(url);

      es.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          const rawKind = payload.kind as NotificationKind;
          if (!rawKind) return;

          if (rawKind === "notification") {
            // DB-persisted notification pushed via SSE
            const notifKind = (payload.notif_kind as NotificationKind) ?? "notification";
            addNotification({
              kind: notifKind,
              notification_id: payload.notification_id,
              title: payload.title,
              message: payload.body || payload.title || buildMessage(notifKind, payload),
              branch_id: payload.branch_id ?? 0,
              entity_type: payload.entity_type,
              entity_id: payload.entity_id,
              payload,
            });
          } else {
            addNotification({
              kind: rawKind,
              message: buildMessage(rawKind, payload),
              branch_id: payload.branch_id ?? 0,
              payload,
            });
          }
        } catch { /* ignore malformed */ }
      };

      es.onerror = () => {
        es?.close();
        es = null;
        if (!active) return;
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      };

      es.onopen = () => { retryDelay = 2000; };
    }

    connect();

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
      if (es) es.close();
    };
  // addNotification is useCallback — stable ref, safe dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
